import React from 'react';
import { CheckIcon, ExternalLinkIcon, LoaderIcon } from 'lucide-react';
import { PROVIDER_ICONS } from '@cotera/client/v0/components/tool-connections/constants';
import {
  CredentialFormCard,
  useToolName,
} from './ask-for-credentials-renderer';
import type { ToolRendererProps } from './index';
import { LoadToolOutput } from './types';
import { Assert } from '@cotera/utilities';
import { HijackerWizardContext } from '../input-hijackers';
import {
  useCocoConversationId,
  useTracking,
  useChatSurface,
  CocoEvent,
} from '@cotera/client/app/etc';
import { useOpenToolModal } from '@cotera/client/v0/components/tool-connections/hooks';
import { coco } from '@cotera/api';
import type { CredentialStrategyInfo } from '@cotera/api';
import { cn } from '@cotera/client/v0/lib/utils';
import { RunningToolTitle } from '@cotera/client/v0/chat/running-tool-title';

const { isAgentToolKey } = coco;

type LoadToolInput = {
  toolKey?: string;
  skillKey?: string;
  t?: string;
  resourceType?: string;
  resourceId?: string;
};

/**
 * Renderer for coco/load-resource (integration tool or Cotera resource).
 *
 * Flow:
 * 1. input-available: Show loading
 * 2. output-available with incomplete + requiresCredentials: Show credential form
 *    (fallback when load-tool swap to ask-for-credentials hasn't persisted yet)
 * 3. output-available: Show success or error
 */
export const LoadToolRenderer: React.FC<ToolRendererProps> = ({ part }) => {
  if (part.isIncomplete()) {
    return <PendingLoadTool part={part} />;
  }

  const input = part.input as LoadToolInput | undefined;
  // output-available
  const output = part.getOutput<LoadToolOutput>();
  if (output === undefined) {
    return null;
  }

  if (output.success) {
    const displayKey = resolveLoadedDisplayKey(input, output.toolKey);
    return (
      <LoadedSuccess
        provider={output.credentialInfo?.provider}
        toolKey={displayKey}
        toolName={output.toolName}
        loadKind={output.loadKind}
      />
    );
  }

  return <LoadError message={output.message} />;
};

export const ConfigureToolRenderer: React.FC<
  ToolRendererProps & {
    wizard?: HijackerWizardContext;
    conversationId?: string;
  }
> = ({ part, chatModel, readonly, wizard, conversationId }) => {
  Assert.assert(part.isIncomplete());
  const output = part.getOutput<LoadToolOutput>();
  Assert.assert(output !== undefined);
  Assert.assert(output.requiresCredentials === true);
  Assert.assert(output.credentialInfo !== undefined);
  Assert.assert(output.credentialInfo.strategies.length > 0);
  const strategy = output.credentialInfo
    .strategies[0] as CredentialStrategyInfo;

  return (
    <CredentialFormCard
      credentialInfo={output.credentialInfo}
      strategy={strategy as CredentialStrategyInfo}
      toolKey={output.toolKey}
      toolName={output.toolName}
      toolCallId={part.toolCallId}
      chatModel={chatModel}
      readonly={readonly ?? false}
      wizard={wizard}
      conversationId={conversationId ?? chatModel.id}
    />
  );
};

/**
 * Handles input-available state.
 * Calls backend, then either completes (success) or shows form (needs credentials).
 */
function resolveLoadedDisplayKey(
  input: LoadToolInput | undefined,
  outputToolKey: string
): string {
  if (input?.t === 'skill' && typeof input.skillKey === 'string') {
    return input.skillKey;
  }
  if (input?.t === 'tool' && typeof input.toolKey === 'string') {
    return input.toolKey;
  }
  if (typeof input?.toolKey === 'string' && input.toolKey.length > 0) {
    return input.toolKey;
  }
  return outputToolKey;
}

const PendingLoadTool: React.FC<{
  part: ToolRendererProps['part'];
}> = ({ part }) => {
  const input = part.input as LoadToolInput | undefined;
  const label =
    input?.t === 'skill' && typeof input.skillKey === 'string'
      ? input.skillKey
      : input?.t === 'tool' && typeof input.toolKey === 'string'
      ? input.toolKey
      : input?.resourceType !== undefined &&
        typeof input.resourceId === 'string'
      ? `${input.resourceType} ${input.resourceId.slice(0, 8)}…`
      : input?.toolKey ?? 'resource';

  // Loading state while checking with backend
  return <LoadingIndicator label={label} />;
};

const LoadingIndicator: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
    <LoaderIcon className="h-4 w-4 animate-spin" />
    <RunningToolTitle className="italic text-sm">
      Loading {label}...
    </RunningToolTitle>
  </div>
);

const LoadedSuccess: React.FC<{
  provider?: string;
  toolKey: string;
  toolName?: string;
  loadKind?: 'tool' | 'resource' | 'skill';
}> = ({ provider, toolKey, toolName, loadKind }) => {
  const isResourceOnly = loadKind === 'resource' && toolKey.trim().length === 0;
  const resolvedName = useToolName(toolKey);
  // `toolName` is typed as a string but older/alternate outputs can omit it —
  // guard before .trim() so the renderer doesn't crash the whole message.
  const toolDisplayName =
    toolName !== undefined && toolName.trim().length > 0
      ? toolName
      : resolvedName ?? toolKey;

  const providerKey =
    provider ?? (toolKey.includes('/') ? toolKey.split('/')[0] : undefined);
  const ProviderIcon =
    providerKey !== undefined && providerKey.length > 0
      ? PROVIDER_ICONS[providerKey]
      : undefined;

  const cocoConversationId = useCocoConversationId();
  const surface = useChatSurface() ?? 'scratch';
  useTracking(
    CocoEvent.ToolLoaded,
    {
      conversationId: cocoConversationId,
      surface,
      toolKey,
      loadKind: loadKind ?? 'tool',
    },
    { enabled: cocoConversationId !== null }
  );

  const openToolModal = useOpenToolModal();
  const isClickable =
    isResourceOnly === false &&
    toolKey.length > 0 &&
    isAgentToolKey(toolKey) === false;

  return (
    <div className="flex items-center gap-2 text-sm italic text-muted-foreground py-1">
      {ProviderIcon !== undefined ? (
        <span className="h-4 w-4 flex items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
          <ProviderIcon />
        </span>
      ) : null}
      <CheckIcon className="h-4 w-4 text-success" />
      {isResourceOnly === true ? (
        <span>Loaded resource</span>
      ) : (
        <span>
          Loaded{' '}
          {isClickable ? (
            <button
              type="button"
              onClick={() => openToolModal(toolKey)}
              className={cn(
                'group/tool-link inline-flex items-center gap-1 italic text-muted-foreground hover:text-foreground cursor-pointer bg-transparent border-0 p-0'
              )}
            >
              {toolDisplayName}
              <ExternalLinkIcon className="h-3 w-3 opacity-0 transition-opacity group-hover/tool-link:opacity-100" />
            </button>
          ) : (
            toolDisplayName
          )}
        </span>
      )}
    </div>
  );
};

const LoadError: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-sm text-muted-foreground py-1">{message}</div>
);
